import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import type { Pageable, WrapperPageableResponse, WrapperResponse } from './service-common';

export type BankStatementLineType = 'CREDIT' | 'DEBIT';
export type BankMovementStatus = 'PENDING' | 'PARTIAL' | 'MATCHED' | 'IGNORED' | 'OUT_OF_SCOPE';
export type BankMatchTargetType = 'SETTLEMENT' | 'CHARGE';
export type BankReconciliationActKind = 'AUTO' | 'SUGGESTED' | 'MANUAL';
export type BankReconciliationActStatus = 'ACTIVE' | 'REVERSED';
export type BankReconciliationAdjustmentType = 'COMMISSION' | 'ROUNDING';

export interface BankImportDTO {
  id?: number;
  bankAccountId?: number;
  originalFilename?: string;
  statementFileId?: number;
  bookedFrom?: string;
  bookedTo?: string;
  insertedCount?: number;
  skippedCount?: number;
  matchedByTrackCode?: number;
  createdAt?: string;
}

export interface BankMovementDTO {
  id?: number;
  bankAccountId?: number;
  bankImportId?: number;
  sourceRow?: number;
  bookedAt?: string;
  lineType?: BankStatementLineType;
  amount?: number;
  description?: string;
  reference?: string;
  counterpartName?: string;
  counterpartClabe?: string;
  trackCode?: string;
  orderingBank?: string;
  paymentFormText?: string;
  paymentFormId?: number;
  movementStatus?: BankMovementStatus;
  actId?: number;
  confidence?: number;
  createdAt?: string;
}

export interface BankInboxItemDTO {
  kind?: BankMatchTargetType;
  id?: number;
  amount?: number;
  date?: string;
  label?: string;
  matched?: boolean;
  /** En un cargo, false cuando aún no está pagado. */
  paid?: boolean;
  suggestionActId?: number;
  confirmedActId?: number;
  candidates?: BankMovementDTO[];
}

export interface BankReconciliationActEntryDTO {
  partyType?: 'CHARGE' | 'SETTLEMENT' | 'MOVEMENT';
  partyId?: number;
  amountAllocated?: number;
}

export interface BankReconciliationActDTO {
  id?: number;
  bankAccountId?: number;
  kind?: BankReconciliationActKind;
  status?: BankReconciliationActStatus;
  confirmed?: boolean;
  confidence?: number;
  reversedAt?: string;
  entries?: BankReconciliationActEntryDTO[];
}

@Injectable({ providedIn: 'root' })
export class BankReconciliationService {
  private readonly http = inject(HttpClient);
  private readonly accountsUrl = `${environment.apiUrl}/bank-accounts`;
  private readonly reconcileUrl = `${environment.apiUrl}/bank-reconcile`;

  importStatement(
    accountId: number,
    body: { name: string; contentType?: string; contentBase64: string; replaceUnmatched?: boolean }
  ): Observable<WrapperResponse<BankImportDTO>> {
    return this.http.post<WrapperResponse<BankImportDTO>>(`${this.accountsUrl}/${accountId}/imports`, body);
  }

  listMovements(
    accountId: number,
    spec: Record<string, unknown> = {},
    pageable: Pageable = { page: 0, size: 50 }
  ): Observable<WrapperPageableResponse<BankMovementDTO>> {
    return this.http.get<WrapperPageableResponse<BankMovementDTO>>(`${this.accountsUrl}/${accountId}/movements`, {
      params: this.buildParams({ ...spec, ...pageable })
    });
  }

  listCompanyMovements(
    spec: Record<string, unknown> = {},
    pageable: Pageable = { page: 0, size: 50 }
  ): Observable<WrapperPageableResponse<BankMovementDTO>> {
    return this.http.get<WrapperPageableResponse<BankMovementDTO>>(`${this.reconcileUrl}/movements`, {
      params: this.buildParams({ ...spec, ...pageable })
    });
  }

  inbox(
    accountId: number,
    spec: Record<string, unknown> = {},
    pageable: Pageable = { page: 0, size: 50 }
  ): Observable<WrapperPageableResponse<BankInboxItemDTO>> {
    return this.http.get<WrapperPageableResponse<BankInboxItemDTO>>(`${this.accountsUrl}/${accountId}/inbox`, {
      params: this.buildParams({ ...spec, ...pageable })
    });
  }

  companyInbox(
    spec: Record<string, unknown> = {},
    pageable: Pageable = { page: 0, size: 50 }
  ): Observable<WrapperPageableResponse<BankInboxItemDTO>> {
    return this.http.get<WrapperPageableResponse<BankInboxItemDTO>>(`${this.reconcileUrl}/inbox`, {
      params: this.buildParams({ ...spec, ...pageable })
    });
  }

  runMatch(accountId: number): Observable<WrapperResponse<BankReconciliationActDTO[]>> {
    return this.http.post<WrapperResponse<BankReconciliationActDTO[]>>(
      `${this.accountsUrl}/${accountId}/run-match`,
      {}
    );
  }

  ignoreMovement(accountId: number, movementId: number): Observable<WrapperResponse<BankMovementDTO>> {
    return this.http.post<WrapperResponse<BankMovementDTO>>(
      `${this.accountsUrl}/${accountId}/movements/${movementId}/ignore`,
      {}
    );
  }

  reactivateMovement(accountId: number, movementId: number): Observable<WrapperResponse<BankMovementDTO>> {
    return this.http.post<WrapperResponse<BankMovementDTO>>(
      `${this.accountsUrl}/${accountId}/movements/${movementId}/reactivate`,
      {}
    );
  }

  deleteMovement(accountId: number, movementId: number): Observable<WrapperResponse<void>> {
    return this.http.delete<WrapperResponse<void>>(
      `${this.accountsUrl}/${accountId}/movements/${movementId}`
    );
  }

  createManual(body: {
    targetType: BankMatchTargetType;
    targetId: number;
    movementIds: number[];
    adjustments?: Array<{ type: BankReconciliationAdjustmentType; amount: number }>;
  }): Observable<WrapperResponse<BankReconciliationActDTO>> {
    return this.http.post<WrapperResponse<BankReconciliationActDTO>>(this.reconcileUrl, body);
  }

  confirm(actId: number): Observable<WrapperResponse<BankReconciliationActDTO>> {
    return this.http.post<WrapperResponse<BankReconciliationActDTO>>(`${this.reconcileUrl}/${actId}/confirm`, {});
  }

  reverse(actId: number): Observable<WrapperResponse<BankReconciliationActDTO>> {
    return this.http.post<WrapperResponse<BankReconciliationActDTO>>(`${this.reconcileUrl}/${actId}/reverse`, {});
  }

  private buildParams(raw: Record<string, unknown>): HttpParams {
    let params = new HttpParams();
    Object.entries(raw).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      if (Array.isArray(value)) {
        value.forEach(item => {
          params = params.append(key, String(item));
        });
        return;
      }
      params = params.append(key, String(value));
    });
    return params;
  }
}
